// ToDo: WW-7459 Move to shared shadow folder
import styles from '../../../../../../../../erp/components/tab-panels/positions/components/edit-table/components/TableRow/TableRow.module.scss';

import clsx from 'clsx';
import produce from 'immer';
import React, { forwardRef, ReactNode, useEffect, useMemo } from 'react';
import { TableCommonProps } from 'react-table';

import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { ServiceContractPositionKind } from '@work4all/models/lib/Enums/ServiceContractPositionKind.enum';

import { reactRefSetter } from '@work4all/utils';

// ToDo: WW-7459 Move to shared shadow folder (4 below)
import { DRAG_HANDLER_COLUMN_WIDTH } from '../../../../../../../../erp/components/tab-panels/positions/components/edit-table/components/TableRow/constants';
import { DragHandlerIconButton } from '../../../../../../../../erp/components/tab-panels/positions/components/edit-table/components/TableRow/DragHandlerIconButton';
import {
  ResizableContext,
  useResizable,
} from '../../../../../../../../erp/components/tab-panels/positions/components/edit-table/components/TableRow/use-resizable';
import { useInifiteList } from '../../../../../../../../erp/components/tab-panels/positions/components/edit-table/InfiniteList';
import {
  DropZoneType,
  EditTableEntry,
  EditTablePositionKind,
} from '../../types';
import { useEditTable } from '../edit-table-provider/EditTableProvider';
import { TableCell } from '../TableCell/TableCell';

import { useEditTableDrag } from './use-edit-table-drag';

export type ITableRowProps = TableCommonProps & {
  disabled?: boolean;
  localId: string;
  id: string;
  index: number;
  onMoveRow?: (
    localId: string,
    index: number,
    dropZoneType: DropZoneType
  ) => void;
  children?: ReactNode;
  kind?: EditTablePositionKind;
  onClickOutside?: (localId: string) => void;
  isNormal?: boolean;
  canDrop: (input: { id: string; index: number }) => number;
  getHovered?: () => EditTableEntry;
  getCurrent?: (id: string) => EditTableEntry;
  isMoveAllowed?: (from: EditTableEntry, to: EditTableEntry) => boolean;
  hasBorder?: {
    vertical: boolean;
    horizontal: boolean;
  };
  isSimpleMode?: boolean;
  isMenuVisible?: boolean;
  isDragHandlerVisible?: boolean;
  isDragHandlerClickable?: boolean;
  onClick?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >['onClick'];
};

const paddingsOfParents = remToPx(0.5);

const TableRow = (
  props: ITableRowProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>
) => {
  const {
    localId,
    index,
    onMoveRow: moveRowDone,
    className,
    style,
    children,
    canDrop,
    getHovered,
    getCurrent,
    isMoveAllowed,
    onClickOutside,
    isNormal = true,
    isSimpleMode,
    isMenuVisible,
    isDragHandlerVisible,
    isDragHandlerClickable,
    kind,
    disabled,
    hasBorder = { vertical: false, horizontal: false },
    ...rest
  } = props;

  const { direction, isDragging, isOver, dropRef, dragRef, dropZone } =
    useEditTableDrag({
      canDrop,
      index,
      localId,
      moveRowDone,
      getHovered,
      getCurrent,
      isMoveAllowed,
      onBeforeDrag: () => {
        onClickOutside?.(localId);
      },
    });

  const modifiedStyle = produce(style, (draft) => {
    draft.width =
      (typeof draft.width === 'number' ? draft.width : parseInt(draft.width)) +
      DRAG_HANDLER_COLUMN_WIDTH;
    draft.opacity = isDragging ? 0 : 1;
  });

  const orientation = direction === 'up' ? '0 4px 0px 0px' : '0 -4px 0px 0px';
  const overStyle: React.CSSProperties = isOver
    ? {
        boxShadow: orientation + ' inset var(--brand01)',
      }
    : {};

  const horizontalWidth = hasBorder?.horizontal ? 1 : 0;
  const verticalWidth = hasBorder?.vertical ? 1 : 0;

  const border: React.CSSProperties = {
    borderBottomStyle: 'solid',
    borderColor: 'var(--ui04)',
    borderTopWidth: horizontalWidth,
    borderBottomWidth: horizontalWidth,
    borderLeftWidth: verticalWidth,
    borderRightWidth: verticalWidth,
    marginTop: 0,
    width: '100%',
    ...overStyle,
  };

  const elId = `position_row_${localId}`;

  useEffect(() => {
    const onClick = (evt) => {
      const flyoutEl = document.getElementById(`${elId}`);
      let targetEl = evt.target as HTMLElement; // clicked element

      // Skip this check for cell clicks that should trigger edit mode
      if (targetEl.closest('.inside-row')) {
        return;
      }

      do {
        if (targetEl === flyoutEl || targetEl.id === elId) {
          return;
        }
        targetEl = targetEl.parentElement;
      } while (targetEl && targetEl?.nodeName !== 'BODY');
      if (targetEl?.nodeName === 'BODY') onClickOutside?.(localId);
    };

    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
    };
  }, [localId, elId, onClickOutside]);

  const { onAddComponent } = useEditTable();
  const handleAdd = (offset = 0) => {
    onAddComponent({
      positionType: ServiceContractPositionKind.TEXTZEILE,
      article: { id: 0 },
      index: index + offset,
      cacheOnly: true,
    });
  };

  const resizable = useResizable();
  const {
    handleElement,
    resizableRef,
    style: resizableStyle,
    height,
  } = resizable;
  const { measureMax } = useInifiteList();

  useEffect(() => {
    // remove static offset of paddings
    if (height) measureMax(index, height - paddingsOfParents);
  }, [index, measureMax, height]);

  const resizableValue = useMemo(() => {
    const setHeight = (e: number | undefined) => {
      if (e === undefined) {
        measureMax(index, null);
        resizable.setHeight(null);
      } else {
        resizable.setHeight(e);
      }
    };

    // Handle auto height rows directly
    const effectiveHeight =
      modifiedStyle?.height === 'auto' ? null : resizable.height;

    return {
      ...resizable,
      height: effectiveHeight,
      setHeight,
    };
  }, [resizable, measureMax, index, modifiedStyle?.height]);

  return (
    <ResizableContext.Provider value={resizableValue}>
      <div
        id={elId}
        key={elId}
        ref={reactRefSetter(forwardedRef, dropRef, resizableRef)}
        className={clsx(styles['table-row'], className, {
          [styles.borderHorizontal]: hasBorder?.vertical,
        })}
        style={{
          ...modifiedStyle,
          ...border,
          ...resizableStyle,
          minHeight: 'calc(2.25rem + 2px)',
        }}
        {...rest}
      >
        {!isMenuVisible || disabled ? null : !moveRowDone ? (
          <TableCell className={styles['drag-empty']} />
        ) : (
          <TableCell
            ref={dragRef}
            role="cell"
            className={clsx(styles['drag-handler'], {
              [styles['drag-handler-simple']]: isSimpleMode,
            })}
            kind={kind}
          >
            {isDragHandlerVisible && (
              <DragHandlerIconButton
                onAddAfter={
                  isNormal && isDragHandlerClickable
                    ? () => {
                        handleAdd(1);
                      }
                    : undefined
                }
                onAddBefore={
                  isNormal && isDragHandlerClickable
                    ? () => {
                        handleAdd();
                      }
                    : undefined
                }
              />
            )}
          </TableCell>
        )}
        {children}
        {handleElement}
      </div>
    </ResizableContext.Provider>
  );
};

const ForwardRefTableRow = forwardRef(TableRow);

export { ForwardRefTableRow as TableRow };
